/**
 * Projeções ANBIMA (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_projecoes_anbima.py).
 *
 * Índice e mês da seção "Revisão de um mês de referência" ficam na URL; a revisão vem
 * de um endpoint próprio (trocar o mês não recarrega o resto da página).
 */
import { Alert, Loader, Select, SimpleGrid, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, CampoOpcoes, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';
const CAMINHO = '/acompanhamentos-periodicos/projecoes-anbima';
interface DadosAnbima {
  fonte: string;
  kpis: KpiApi[];
  meses: { valor: string; rotulo: string }[];
  barras: ({ grafico: GraficoApi } | { aviso: string })[];
  rodape: string;
  ultimas: TabelaApi;
}
type DadosRevisao =
  | { indice: string; mes: string; aviso: string }
  | {
      indice: string;
      mes: string;
      grafico: GraficoApi;
      legenda: string | null;
      tabela: TabelaApi;
    };
function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}
export function ProjecoesAnbima() {
  const [filtros, definir] = useFiltrosUrl({ indice: 'IPCA', mes: '' });
  const pagina = useQuery({
    queryKey: ['acompanhamentos.anbima'],
    queryFn: ({ signal }) => apiGet<Envelope<DadosAnbima>>(CAMINHO, {}, signal),
  });
  const revisao = useQuery({
    queryKey: ['acompanhamentos.anbima.revisao', filtros.indice, filtros.mes],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosRevisao>>(
        `${CAMINHO}/revisao`,
        { indice: filtros.indice, mes: filtros.mes || undefined },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
  const dados = pagina.data?.data;
  const rev = revisao.data?.data;
  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Projeções ANBIMA"
        descricao="Variação mensal do IPCA e do IGP-M projetada pelo Comitê Macroeconômico da ANBIMA. Cada mês nasce como projeção do mês posterior e é revisto como mês corrente."
        dadosAte={pagina.data?.meta.dados_ate}
        fonte="ANBIMA Feed"
      >
        {dados && (
          <Text size="xs" c="dimmed">
            {dados.fonte}
          </Text>
        )}
      </CabecalhoPagina>
      <BarraFiltros carregando={pagina.isFetching || revisao.isFetching}>
        <GrupoFiltros titulo="Revisão de um mês">
          <CampoOpcoes
            rotulo="Índice"
            valor={filtros.indice}
            aoMudar={(indice) => definir({ indice })}
            opcoes={[
              { valor: 'IPCA', rotulo: 'IPCA' },
              { valor: 'IGP-M', rotulo: 'IGP-M' },
            ]}
          />
          <Select
            label="Mês de referência"
            allowDeselect={false}
            disabled={!dados}
            data={(dados?.meses ?? []).map((m) => ({ value: m.valor, label: m.rotulo }))}
            value={filtros.mes || rev?.mes || null}
            onChange={(mes) => mes && definir({ mes })}
            w={LARGURA.numero}
          />
        </GrupoFiltros>
      </BarraFiltros>
      {pagina.isPending && (
        <Text size="sm" c="dimmed">
          <Loader size="xs" mr={8} />
          Carregando as projeções no ANBIMA Feed (a primeira consulta pode levar até um minuto).
        </Text>
      )}
      {pagina.isError && (
        <Alert color="red">{mensagem(pagina.error, 'Não foi possível carregar os dados.')}</Alert>
      )}
      {dados && (
        <>
          <AvisosApi avisos={[pagina.data?.meta.avisos, revisao.data?.meta.avisos]} />
          <Secao
            titulo="Indicadores chave"
            legenda="Última coleta do mês corrente e do posterior, com a revisão desde a coleta anterior."
          >
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
              {dados.kpis.map((k) => (
                <Kpi key={k.rotulo} kpi={k} />
              ))}
            </SimpleGrid>
          </Secao>
          <Secao
            titulo="Revisão de um mês de referência"
            legenda="Cada ponto é uma coleta da variação projetada daquele mês, em %. O eixo não começa em zero para a revisão entre coletas aparecer."
          >
            {revisao.isError && (
              <Alert color="red">{mensagem(revisao.error, 'Erro ao carregar a revisão.')}</Alert>
            )}
            {rev &&
              ('aviso' in rev ? (
                <Alert color="blue">{rev.aviso}</Alert>
              ) : (
                <>
                  <Grafico grafico={rev.grafico} />
                  {rev.legenda && (
                    <Text size="xs" c="dimmed">
                      {rev.legenda}
                    </Text>
                  )}
                  <TabelaDados tabela={rev.tabela} alturaAutomatica />
                </>
              ))}
          </Secao>
          <Secao
            titulo="Primeira coleta contra a última"
            legenda="IPCA e IGP-M ficam em gráficos separados: a revisão do IGP-M chega a vários pontos percentuais e achataria o IPCA no mesmo eixo."
          >
            <SimpleGrid cols={{ base: 1, lg: 2 }}>
              {dados.barras.map((b, i) =>
                'grafico' in b ? (
                  <Grafico key={b.grafico.id} grafico={b.grafico} />
                ) : (
                  <Alert key={i} color="blue">
                    {b.aviso}
                  </Alert>
                ),
              )}
            </SimpleGrid>
            <Text size="xs" c="dimmed">
              {dados.rodape}
            </Text>
          </Secao>
          <Secao
            titulo={`Últimas coletas (${dados.ultimas.linhas.length})`}
            legenda="Coletas mais recentes do ANBIMA Feed, dos dois índices."
          >
            <TabelaDados tabela={dados.ultimas} alturaAutomatica />
          </Secao>
        </>
      )}
    </Pagina>
  );
}
